import {
  getPinBgColor,
  getPinIconColor,
  pinBodySvg,
  knockStatusBadgeSvg,
  PIN_W,
  PIN_H,
  PIN_ANCHOR_X,
  PIN_ANCHOR_Y,
  KS_W,
  KS_H,
  KS_TIP_X,
  KS_TIP_Y,
  KS_ANCHOR_X,
  KS_ANCHOR_Y,
} from './pinSvg'
import { getMapIcon } from './iconCache'
import { EntityLocationDTO } from '@user/store/map/typings'
import { MapPinVariant, MapPinTypeVariant } from '../MapPin/types'

export const getVariantFromEntityType = (
  entityType: string | null | undefined,
): MapPinVariant => {
  switch ((entityType ?? '').toLowerCase()) {
    case 'opportunity':
      return MapPinTypeVariant.Opportunity
    case 'lead':
      return MapPinTypeVariant.Lead
    case 'pendinglead':
      return MapPinTypeVariant.PendingLead
    case 'draftlead':
      return MapPinTypeVariant.DraftLead
    case 'pestpaclocation':
      return MapPinTypeVariant.PestPac
    case 'current_location':
      return MapPinTypeVariant.CurrentLocation
    default:
      return MapPinTypeVariant.Lead
  }
}

// Composite canvas: pin body (27×56) + KS badge (35×35) overlaid at top-left.
// Pin is positioned so its vertical centerline matches the badge pointer tip.
const COMP_W = 35
const COMP_PIN_X = KS_TIP_X - PIN_ANCHOR_X
const PIN_TOP_Y = 0.5
const BADGE_TO_PIN_GAP = 0
const COMP_PIN_Y = KS_TIP_Y + BADGE_TO_PIN_GAP - PIN_TOP_Y
const COMP_H = Math.ceil(COMP_PIN_Y + PIN_H)
const COMP_ANCHOR_X = COMP_PIN_X + PIN_ANCHOR_X // bottom-centre of pin in composite coords
const COMP_ANCHOR_Y = COMP_PIN_Y + PIN_ANCHOR_Y

function stripOuterSvg(svgString: string): string {
  return svgString.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '')
}

export function buildIconSvg(
  location: EntityLocationDTO,
  variant: MapPinVariant,
  selected: boolean,
  isD2dEnabled: boolean,
): { svg: string; w: number; h: number; ax: number; ay: number } {
  const ksColor = isD2dEnabled
    ? (location.knockStatusSettingColor ?? null)
    : null
  const ksIcon = isD2dEnabled ? (location.knockStatusSettingIcon ?? null) : null
  const isOnlyKnockStatus =
    location.entityId === 0 &&
    variant !== MapPinTypeVariant.CurrentLocation &&
    variant !== MapPinTypeVariant.PestPac

  if (isOnlyKnockStatus && ksColor) {
    return {
      svg: knockStatusBadgeSvg(ksColor, ksIcon),
      w: KS_W,
      h: KS_H,
      ax: KS_ANCHOR_X,
      ay: KS_ANCHOR_Y,
    }
  }

  const effectiveVariant = ksIcon === 0 ? MapPinTypeVariant.DoNotKnock : variant
  const bgColor = getPinBgColor(
    effectiveVariant,
    selected,
    location.multitenant,
    location.isLost ?? false,
  )
  const iconColor = getPinIconColor(effectiveVariant, location.multitenant)
  const isGroup = location.multitenant && (location.count ?? 1) > 1

  // SH-1326: multi-record pins get the badge; isGroup keeps their group icon.
  if (ksColor) {
    const pinInner = stripOuterSvg(
      pinBodySvg(variant, bgColor, iconColor, isGroup),
    )
    const badgeInner = stripOuterSvg(knockStatusBadgeSvg(ksColor, ksIcon))
    // Use <g transform> instead of nested <svg viewBox> — HERE Maps HARP does not
    // correctly apply fill colours inside a nested <svg> that has viewBox coordinate scaling.
    const badgeScale = KS_W / 50
    const svg = [
      `<svg xmlns="http://www.w3.org/2000/svg" width="${COMP_W}" height="${COMP_H}">`,
      `<g transform="translate(${COMP_PIN_X},${COMP_PIN_Y})">`,
      pinInner,
      `</g>`,
      `<g transform="scale(${badgeScale})">`,
      badgeInner,
      `</g>`,
      `</svg>`,
    ].join('')
    return { svg, w: COMP_W, h: COMP_H, ax: COMP_ANCHOR_X, ay: COMP_ANCHOR_Y }
  }

  return {
    svg: pinBodySvg(variant, bgColor, iconColor, isGroup),
    w: PIN_W,
    h: PIN_H,
    ax: PIN_ANCHOR_X,
    ay: PIN_ANCHOR_Y,
  }
}

export function createStaticMarker(
  H: any,
  location: EntityLocationDTO,
  variant: MapPinVariant,
  selected: boolean,
  isD2dEnabled: boolean,
): H.map.Marker {
  const coords = { lat: location.latitude, lng: location.longitude }
  let currentSelected = selected
  let markerRef: H.map.Marker | null = null

  const buildIcon = (sel: boolean) => {
    const { svg, w, h, ax, ay } = buildIconSvg(
      location,
      variant,
      sel,
      isD2dEnabled,
    )
    return getMapIcon(H, svg, w, h, ax, ay)
  }

  const setSelected = (next: boolean) => {
    if (currentSelected === next) return
    currentSelected = next
    if (markerRef) {
      markerRef.setIcon(buildIcon(next))
      const data = markerRef.getData() ?? {}
      markerRef.setData({ ...data, selected: next })
    }
  }

  const marker = new H.map.Marker(coords, { icon: buildIcon(selected) })
  markerRef = marker

  marker.setData({
    contactLocation: location,
    variant,
    coords,
    selected: currentSelected,
    setSelected,
    __cleanup: () => {
      markerRef = null
    },
  })

  return marker
}
